import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import DetailLoadingSkeleton from "@/components/shared/DetailLoadingSkeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Phone,
  AlertCircle,
  Pencil,
  Package,
  ShoppingCart,
  Loader2,
  Trash,
  BookOpen,
} from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { formatNumber, formatMoney, formatDate } from "@/lib/utils";
import type { Vendor, OutsourcePO } from "@/shared/schemas";
import { useState, ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { InteractiveStatusSwitch } from "@/components/ui/interactive-status-switch";
import RelatedDataTabs from "@/components/shared/RelatedDataTabs";
import { EntityLink } from "@/components/shared/EntityLink";
import { DetailField as F } from "@/components/shared/DetailField";

const fmtD = (d: string | Date | null | undefined) => formatDate(d) || "—";


export default function VendorDetailPage({ id }: { id?: string } = {}) {
  const params = useParams({ strict: false }) as { vendorId?: string };
  const vendorId = id || params.vendorId || "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [subTab, setSubTab] = useState("materials");

  const {
    data: vendorData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["vendor", vendorId],
    queryFn: async () => {
      const result = await trpcClient.vendors.getById.query({ id: vendorId });
      return result as Vendor;
    },
  });

  const { data: posData, isLoading: isLoadingPos } = useQuery({
    queryKey: ["vendor-pos", vendorId],
    queryFn: async () => {
      const result = await trpcClient.outsourcePos.list.query({
        vendorId,
        limit: 100,
      });
      return (result as any).items as OutsourcePO[];
    },
    enabled: !!vendorId,
  });

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await trpcClient.vendors.delete.mutate({ id: vendorId });
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      await queryClient.invalidateQueries({ queryKey: ["activity-log"] });
      await queryClient.invalidateQueries({ queryKey: ["vendors-lookup"] });

      navigate({ to: "/vendors" });
    } catch (err: any) {
      toast.error(
        err?.message ||
          "Xóa thất bại. NCC này có thể đang ràng buộc dữ liệu đơn gia công.",
      );
      setIsDeleting(false);
    }
  };

  const vendor = vendorData as any;
  const vendorPos = posData || [];
  const materialsList: any[] = vendor?.materials || [];

  if (isLoading) {
    return <DetailLoadingSkeleton breadcrumbs={[{ label: "Nhà cung cấp", to: "/vendors" }]} />;
  }

  if (error || !vendor) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Nhà cung cấp", to: "/vendors" }]}
        title="Không tìm thấy"
      >
        <div className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            Không tìm thấy nhà cung cấp
          </h3>
          <p className="text-slate-500 mb-4">Mã {vendorId} không tồn tại</p>
          <Button onClick={() => navigate({ to: "/vendors" })}>
            Quay lại danh sách
          </Button>
        </div>
      </StandardDetailLayout>
    );
  }

  return (
    <StandardDetailLayout
      breadcrumbs={[{ label: "Nhà cung cấp", to: "/vendors" }]}
      title={vendor.code || vendorId}
      entityId={vendor?.id || vendorId}
      entityType="vendor"
      timestamps={{ created: vendor.createdAt, updated: vendor.updatedAt }}
      status={
        <div className="flex items-center gap-2">
          <Badge
            variant="secondary"
            className="bg-blue-600 text-white hover:bg-blue-700 text-[10px] shrink-0"
          >
            {vendor.type?.replace("_", " ") || "NCC"}
          </Badge>
          <InteractiveStatusSwitch
            initialStatus={vendor.status}
            entityName="nhà cung cấp này"
            onToggle={async (newStatus) => {
              await trpcClient.vendors.update.mutate({
                id: vendor.id,
                data: { status: newStatus },
              });
              queryClient.invalidateQueries({ queryKey: ["vendor", vendorId] });
            }}
          />
        </div>
      }
      actions={
        <>
          <Button variant="outline" size="sm" className="bg-white">
            <Phone size={14} className="mr-1.5" /> Gọi điện
          </Button>
          <Button
            variant="default"
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() =>
              navigate({ to: "/vendors/$vendorId/edit", params: { vendorId } })
            }
          >
            <Pencil size={14} className="mr-1.5" /> Chỉnh sửa
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-rose-600 border-rose-200 hover:bg-rose-50"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Trash size={14} className="mr-1.5" /> Xóa
          </Button>
        </>
      }
      onBack={() => navigate({ to: "/vendors" })}
      showContextPanel
    >
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Xóa nhà cung cấp này?"
        description={
          <>
            Bạn có chắc chắn muốn xóa NCC{" "}
            <strong>{vendor.name || vendor.shortName}</strong> không? Hành động
            này không thể hoàn tác.
          </>
        }
        confirmText="Xóa ngay"
        onConfirm={handleDelete}
        isLoading={isDeleting}
        variant="destructive"
      />
      {/* ═══ FLAT INFO — Layout ═══ */}
      <div className="flex flex-col xl:flex-row gap-5 mb-5">
        {/* ── LEFT: CƠ BẢN & GIAO DỊCH ── */}
        <div className="flex-1 bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
          {/* THÔNG TIN CƠ BẢN */}
          <div>
            <h4 className="text-[12px] font-bold text-blue-600 mb-3 uppercase tracking-wide">
              Thông tin cơ bản
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
              <F label="Mã NCC" value={vendor.code} mono />
              <F label="Tên viết tắt" value={vendor.shortName} />

              <F
                label="Loại hình"
                value={
                  <Badge
                    variant="outline"
                    className="font-normal text-xs bg-slate-100 text-slate-600 border-transparent shadow-none px-2 py-0"
                  >
                    {vendor.type?.replace("_", " ").toUpperCase() || "NCC"}
                  </Badge>
                }
              />
              <F
                label="Phân khúc"
                value={
                  vendor.group ? (
                    <Badge
                      variant="outline"
                      className="font-medium text-[10px] uppercase bg-blue-600 text-white border-transparent shadow-none px-1.5 py-0"
                    >
                      {vendor.group}
                    </Badge>
                  ) : undefined
                }
              />

              <F label="Điện thoại" value={vendor.phone} />
              <F label="Email" value={vendor.email} />

              <F label="Mã số thuế" value={vendor.taxCode} mono hl />
              <F label="Website" value={vendor.website} />

              <F
                label="Dịch vụ"
                value={
                  vendor.services?.length
                    ? vendor.services
                        .map((s: string) => s.replace("_", " "))
                        .join(", ")
                    : "—"
                }
                className="lg:col-span-2"
              />

              <F label="Địa chỉ" value={vendor.address} />
              <F label="Địa chỉ giao hàng" value={vendor.deliveryAddress} />
            </div>
          </div>

          <div className="border-b border-slate-100 my-2" />

          {/* ĐẠI DIỆN & GIAO DỊCH */}
          <div>
            <h4 className="text-[12px] font-bold text-blue-600 mb-3 uppercase tracking-wide">
              Đại diện & Giao dịch
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
              <F label="Người đại diện" value={vendor.representative} />
              <F label="Người liên hệ" value={vendor.contactPerson} />

              <F
                label="Số điện thoại LH"
                value={vendor.mobile || vendor.phone}
              />
              {vendor.transitAddress && (
                <F label="Trung chuyển" value={vendor.transitAddress} />
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT: NGÂN HÀNG & QUẢN LÝ ── */}
        <div className="w-full xl:w-[320px] shrink-0 bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
          {/* NGÂN HÀNG & CÔNG NỢ */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-[12px] font-bold text-blue-600 uppercase tracking-wide">
                Ngân hàng & Công nợ
              </h4>
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-[11px] gap-1 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 border-indigo-200"
                onClick={() => navigate({ to: `/finance/ap/ledger/${(vendorData as any)?.id || vendorId}` as any })}
                title="Mở Sổ cái chi tiết công nợ phải trả"
              >
                <BookOpen className="h-3 w-3" /> Sổ cái
              </Button>
            </div>
            <div className="flex flex-col gap-1">
              <F label="Số tài khoản" value={vendor.bankAccountNumber} mono />
              <F label="Tên tài khoản" value={vendor.bankAccountHolder} />
              <F label="Ngân hàng" value={vendor.bankName} />
              <F
                label="Nợ khởi tạo"
                value={
                  vendor.initialDebt
                    ? formatMoney(vendor.initialDebt) + " đ"
                    : "0 đ"
                }
                mono
              />
              <F
                label="Hạn mức CN"
                value={
                  vendor.tags ? formatMoney(Number(vendor.tags)) + " đ" : "0 đ"
                }
                mono
              />
            </div>
          </div>

          <div className="border-b border-slate-100 my-2" />

          {/* QUẢN LÝ */}
          <div>
            <h4 className="text-[12px] font-bold text-blue-600 mb-3 uppercase tracking-wide">
              Quản lý
            </h4>
            <div className="flex flex-col gap-1">
              {vendor.notes && (
                <F
                  label="Ghi chú"
                  value={vendor.notes}
                  valueClass="text-orange-600"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Tabs: Vật tư | Đơn gia công ═══ */}
      <div className="border border-slate-200 rounded-lg bg-white shadow-sm overflow-hidden">
        <Tabs value={subTab} onValueChange={setSubTab}>
          <TabsList>
            <TabsTrigger
              value="materials"
            >
              <Package className="h-3.5 w-3.5 mr-1.5" />
              Vật tư{" "}
              {materialsList.length > 0 && (
                <span className="ml-1 text-xs bg-slate-100/80 text-slate-600 px-1.5 py-0.5 rounded-full">
                  {materialsList.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="pos"
            >
              <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />
              Đơn gia công{" "}
              {vendorPos.length > 0 && (
                <span className="ml-1 text-xs bg-slate-100/80 text-slate-600 px-1.5 py-0.5 rounded-full">
                  {vendorPos.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── Materials Tab ── */}
          <TabsContent value="materials" className="p-3">
            {materialsList.length === 0 ? (
              <div className="text-center text-sm text-slate-400 py-6">
                Chưa có vật tư liên kết
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mã VT</TableHead>
                      <TableHead>Tên vật tư</TableHead>
                      <TableHead>Nhóm</TableHead>
                      <TableHead>Loại</TableHead>
                      <TableHead>ĐVT</TableHead>
                      <TableHead className="text-right">Đơn giá</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materialsList.map((m: any) => (
                      <TableRow
                        key={m.id}
                        className="hover:bg-slate-50"
                      >
                        <TableCell className="font-mono text-xs font-medium">
                          <EntityLink
                            entityType="material"
                            id={m.id}
                            code={m.code}
                          />
                        </TableCell>
                        <TableCell className="text-xs break-words whitespace-normal max-w-[200px]">
                          <EntityLink
                            entityType="material"
                            id={m.id}
                            name={m.name}
                          />
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">
                          {m.category || "—"}
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">
                          {m.materialType?.label || "—"}
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">
                          {m.unit || "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">
                          {formatMoney(m.price)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* ── Outsource POs Tab ── */}
          <TabsContent value="pos" className="p-3">
            {isLoadingPos ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
              </div>
            ) : vendorPos.length === 0 ? (
              <div className="text-center text-sm text-slate-400 py-6">
                Chưa có đơn gia công
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mã PO</TableHead>
                      <TableHead>Sản phẩm</TableHead>
                      <TableHead className="text-right">Số lượng</TableHead>
                      <TableHead className="text-right">
                        Thành tiền (VNĐ)
                      </TableHead>
                      <TableHead className="text-center">Trạng thái</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vendorPos.map((po) => {
                      const poAny = po as any;
                      return (
                        <TableRow
                          key={po.poId}
                          className="hover:bg-gray-50"
                        >
                          <TableCell className="font-mono text-xs font-medium">
                            <EntityLink
                              entityType="outsourceOrder"
                              id={poAny.id || po.poId}
                              code={po.poId}
                            />
                          </TableCell>
                          <TableCell>
                            {poAny.productId || poAny.product?.id ? (
                              <EntityLink
                                entityType="product"
                                id={poAny.productId || poAny.product?.id}
                                name={poAny.product?.name || poAny.productName || "—"}
                              />
                            ) : (
                              poAny.product?.name || poAny.productName || "—"
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatNumber(po.qtyOrdered)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-bold">
                            {formatMoney(po.totalAmount)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="text-xs">
                              {po.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow className="bg-slate-50 font-bold">
                      <TableCell
                        colSpan={3}
                        className="text-right text-gray-600 uppercase text-xs"
                      >
                        Tổng cộng (VNĐ)
                      </TableCell>
                      <TableCell className="text-right text-indigo-600 text-base tabular-nums">
                        {formatMoney(
                          vendorPos.reduce(
                            (acc, po) => acc + Number(po.totalAmount || 0),
                            0,
                          ),
                        )}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </StandardDetailLayout>
  );
}
